// @flow

import * as React from 'react';
import NumberUtil from 'app/common/lib/NumberUtil';
import { currencySymbols } from 'app/common/lib/locale';
import { TextField } from 'gestalt';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';

type HOCProps = {|
  requestContext: RequestContextType,
|};

type OwnProps = {|
  autoComplete?: 'on' | 'off',
  currencyCode?: string,
  elementType?: number,
  errorMessage?: string,
  hasFractionalCents?: boolean,
  id: string,
  onValueParsed: (value: ?number) => void,
  placeholder?: string,
  roundValue?: boolean,
  valueInMicroCurrency: ?number,
|};

type Props = {
  ...HOCProps,
  ...OwnProps,
};

type State = {
  currentValue: string,
  isEditing: boolean,
};

function formatCurrencyValue(
  valueInMicroCurrency: number,
  hasFractionalCents: ?boolean,
  locale: string,
  roundValue?: boolean,
) {
  let formattedCurrencyValue = NumberUtil.formatBasedOnLocale(
    NumberUtil.microCurrencyToCurrency(valueInMicroCurrency, !!hasFractionalCents),
    locale,
    !!hasFractionalCents,
  );
  if (roundValue) {
    formattedCurrencyValue = formattedCurrencyValue.slice(0, -3);
  }
  return formattedCurrencyValue;
}

/**
 * This currency field supports parsing based on locale. In other words, this field
 * will be able to handle commas, empty spaces as number separators.
 * The parsed value will be returned to the callback function in micro currency if
 * the input can be evaluated as a number.
 */
export class CurrencyTextField extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    const {
      valueInMicroCurrency,
      requestContext,
      currencyCode,
      hasFractionalCents,
      roundValue,
    } = props;
    const currencySign = currencyCode ? currencySymbols[currencyCode] || '' : '';
    let currentValue = '';
    if (!!valueInMicroCurrency) {
      const formattedCurrencyValue = formatCurrencyValue(
        valueInMicroCurrency,
        hasFractionalCents,
        requestContext.locale,
        roundValue,
      );
      currentValue = `${currencySign}${formattedCurrencyValue}`;
    }
    this.state = { isEditing: false, currentValue };
  }

  // update the currentValue state here if there is any incoming change in value in
  // props so we can have the right value in type string to display in the Textfield.
  // setState cannot be called in render() so it has to be done in componentWillReceiveProps.
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      this.props.valueInMicroCurrency !== nextProps.valueInMicroCurrency ||
      (nextProps.valueInMicroCurrency != null && this.state.currentValue === '') ||
      this.props.hasFractionalCents !== nextProps.hasFractionalCents
    ) {
      this.setState({
        currentValue: this.formatValue(
          nextProps.valueInMicroCurrency,
          this.state.currentValue,
          !!nextProps.hasFractionalCents,
          nextProps.roundValue,
        ),
      });
    }
  }

  onHandleChange = ({ value }: { value: string }) => {
    this.setState({ currentValue: value });
  };

  setEditState = () => {
    this.setState({ isEditing: !this.state.isEditing }, this.parseValue);
  };

  // parse the value in the field once the user is done editing.
  // if the current value is empty => parsed value = null
  // if the current value evaluates to a NaN => parsed value = Number.MIN_VALUE
  // if the current value evaluates to a number => parsed value = number
  parseValue = () => {
    const { currentValue, isEditing } = this.state;
    const { hasFractionalCents, roundValue } = this.props;
    // Don't format the value if the user is still editing.
    if (isEditing) {
      return;
    }

    const { requestContext, onValueParsed } = this.props;
    const locale = requestContext.locale;

    let parsedValue = NumberUtil.parseNumberBasedOnLocale(
      currentValue,
      locale,
      !!hasFractionalCents,
    );

    if (currentValue === '') {
      parsedValue = null;
    } else if (isNaN(parsedValue)) {
      parsedValue = Number.MIN_VALUE;
    } else {
      parsedValue = NumberUtil.currencyToMicroCurrency(parsedValue, !!hasFractionalCents);
    }

    this.setState({
      currentValue: this.formatValue(parsedValue, currentValue, !!hasFractionalCents, roundValue),
    });

    onValueParsed(parsedValue);
  };

  /**
   * Converts a microcurrency into a formatted currency string
   * based on locale.
   * example: parsedValue = 1000000000 => '1,000' for en-US
   *
   * if the input number is Number.MIN_VALUE, it means the input from the user
   * cannot be parsed into a valid number, therefore we just return whatever that
   * input is.
   * example: currentValue = 'abcde' => 'abcde'
   */
  formatValue = (
    parsedValue: ?number,
    currentValue: string,
    hasFractionalCents: boolean,
    roundValue?: boolean,
  ): string => {
    const { requestContext, currencyCode } = this.props;
    const { isEditing } = this.state;
    let formattedValue = '';
    const currencySign = currencyCode ? currencySymbols[currencyCode] || '' : '';

    if (isEditing) {
      formattedValue = currentValue;
    } else if (parsedValue === Number.MIN_VALUE) {
      formattedValue = currentValue;
    } else if (parsedValue === null || parsedValue === undefined) {
      formattedValue = '';
    } else {
      const formattedCurrencyValue = formatCurrencyValue(
        parsedValue,
        hasFractionalCents,
        requestContext.locale,
        roundValue,
      );
      formattedValue = `${currencySign}${formattedCurrencyValue}`;
    }

    return formattedValue;
  };

  render() {
    const {
      id,
      autoComplete,
      placeholder,
      errorMessage,

      valueInMicroCurrency,
      hasFractionalCents,
      roundValue,
    } = this.props;
    const { currentValue } = this.state;

    return (
      <TextField
        id={id}
        autoComplete={autoComplete}
        onChange={this.onHandleChange}
        onBlur={this.setEditState}
        onFocus={this.setEditState}
        placeholder={placeholder}
        errorMessage={errorMessage}
        value={
          currentValue ||
          this.formatValue(valueInMicroCurrency, currentValue, !!hasFractionalCents, roundValue)
        }
      />
    );
  }
}

export default withRequestContext(CurrencyTextField);
